// use-section-animation.ts — mounts a SectionModule when its section root first
// intersects the viewport, tears it down on unmount. IntersectionObserver rather
// than mount-time: this page has ten animated sections and starting all of them on
// load is what would make it the heaviest LP in the registry.
import { type RefObject, useEffect, useRef } from 'react';

import { createClock, prefersReducedMotion } from './engine';
import type { HudStrings } from './strings';
import { HUD_STRINGS_EN } from './strings';

const INTERSECTION_THRESHOLD = 0.15;
const ANIM_RUNNING_ATTRIBUTE = 'data-anim-running';

export type SectionModule = (context: { root: HTMLElement; strings: HudStrings; clock: ReturnType<typeof createClock> }) => () => void;

// `strings` defaults to the English constant so the nine sections that never needed
// localised HUD copy keep calling this with one argument. The Multi Internet section
// passes its translated set (SCRUM-1872).
export function useSectionAnimation(module: SectionModule, strings: HudStrings = HUD_STRINGS_EN): RefObject<HTMLElement> {
  const ref = useRef<HTMLElement>(null);
  const moduleRef = useRef(module);
  moduleRef.current = module;
  // Read through a ref: the mount effect runs once, and a new `t` identity on
  // re-render must not remount a running animation.
  const stringsRef = useRef(strings);
  stringsRef.current = strings;

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;

    let teardown: (() => void) | null = null;
    let clock: ReturnType<typeof createClock> | null = null;

    const mount = () => {
      if (teardown) return; // StrictMode double-invoke guard: never mount twice.
      // Each section that calls this hook gets its own createClock() instance — this
      // is NOT a shared master clock across the page's sections (see engine.ts).
      clock = createClock();
      teardown = moduleRef.current({ root, strings: stringsRef.current, clock });
      // A loop only actually runs when the visitor has not asked for reduced motion —
      // createClock() invokes subscribers once synchronously otherwise, with no rAF.
      // This attribute is Task 15's only externally observable signal of a live loop.
      if (!prefersReducedMotion()) root.setAttribute(ANIM_RUNNING_ATTRIBUTE, 'true');
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            mount();
            observer.disconnect();
          }
        }
      },
      { threshold: INTERSECTION_THRESHOLD }
    );
    observer.observe(root);

    return () => {
      observer.disconnect();
      if (teardown) teardown();
      clock?.stop();
      root.removeAttribute(ANIM_RUNNING_ATTRIBUTE);
    };
  }, []);

  return ref;
}
